import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { throttle } from 'underscore';

import { GeodataBoundaryPolygonModel } from '../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { iiafe } from '../../../utils/async';
import { isDefined } from '../../../utils/variables';
import { SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { useSubscribeCallback } from '../../hooks/use-subscribe';

async function getBoundaryPolygons(geodataId: string): Promise<GeodataBoundaryPolygonModel[]> {
  const response = await SkyMapAxiosServiceFactory.instance
    .createGeodataServiceV0()
    .getBoundaryPolygons({
      path: {
        geodataId,
      },
    });

  return response.data;
}

export function useBoundaryPolygons(geodataId: string, options?: { enabled?: boolean }) {
  const { handleError, clearErrors, buildErrorList } = useErrorHandling();
  const enabled = options?.enabled ?? true;

  const { data, refetch, error, isPending, isRefetching } = useQuery({
    queryKey: ['getBoundaryPolygons', geodataId],
    queryFn: async () => getBoundaryPolygons(geodataId),
    enabled,
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: 1000 * 60 * 2,
    staleTime: 1000 * 60 * 2,
  });

  const throttledRefetch = useMemo(
    () => throttle(refetch, 100, { leading: false, trailing: true }),
    [refetch],
  );

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useSubscribeCallback(SubscriptionTopic.WebsocketMessageRecieved, (event) => {
    if (enabled && event.message === 'BoundaryPolygonUpdated' && event.geodataId === geodataId) {
      iiafe(async () => {
        await throttledRefetch();
      });
    }
  });

  useEffect(() => {
    if (isDefined(error)) {
      handleError(error, { retryCallback: () => void refetchRef.current() });
    } else {
      clearErrors();
    }
  }, [handleError, clearErrors, error]);

  const errorList = buildErrorList();

  const boundaryPolygons = useMemo(() => {
    return data || [];
  }, [data]);

  return {
    boundaryPolygons,
    refetch: throttledRefetch,
    errorList,
    isPending: isPending || isRefetching,
  };
}
